import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart,
  Activity,
  Loader2,
  Wifi,
  WifiOff,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Play,
  Download,
  PenLine,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useOssAuth } from "../context/OssAuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLASSIFICATION_STYLES = {
  optimal:  { bg: "bg-emerald-50",  border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-500" },
  normal:   { bg: "bg-green-50",    border: "border-green-200",   text: "text-green-700",   badge: "bg-green-500" },
  elevated: { bg: "bg-yellow-50",   border: "border-yellow-200",  text: "text-yellow-700",  badge: "bg-amber-500" },
  moderate: { bg: "bg-orange-50",   border: "border-orange-200",  text: "text-orange-700",  badge: "bg-orange-500" },
  high:     { bg: "bg-red-50",      border: "border-red-200",     text: "text-red-700",     badge: "bg-red-600" },
  critical: { bg: "bg-red-100",     border: "border-red-300",     text: "text-red-800",     badge: "bg-red-700" },
};

function getTrend(misurazioni) {
  if (!misurazioni || misurazioni.length < 2) return null;
  const diff = (misurazioni[0]?.sistolica ?? 0) - (misurazioni[1]?.sistolica ?? 0);
  if (diff > 5) return "up";
  if (diff < -5) return "down";
  return "stable";
}

function toLocalDatetimeValue(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export default function OssWithingsPressione({ fascicoloId }) {
  const { token } = useOssAuth();
  const [connected, setConnected] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  const [misurazioni, setMisurazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingLatest, setFetchingLatest] = useState(false);
  const [measureStartTs, setMeasureStartTs] = useState(null);
  const [polling, setPolling] = useState(false);

  const [manualSys, setManualSys] = useState("");
  const [manualDia, setManualDia] = useState("");
  const [manualHr, setManualHr] = useState("");
  const [manualWhen, setManualWhen] = useState(() => toLocalDatetimeValue());
  const [savingManual, setSavingManual] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const checkStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/oss/me/withings/status`, { headers: { Authorization: `Bearer ${token}` } });
      setConnected(res.data.connected);
      setConnectedAt(res.data.connected_at || null);
    } catch {
      setConnected(false);
    }
  }, [token]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/oss/me/withings/measures/${fascicoloId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMisurazioni(res.data.misurazioni || []);
    } catch {
      // silent
    }
  }, [fascicoloId, token]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await checkStatus();
      await loadHistory();
      setLoading(false);
    };
    init();
  }, [checkStatus, loadHistory]);

  const handleStartMeasure = () => setMeasureStartTs(Math.floor(Date.now() / 1000));
  const handleCancelMeasure = () => setMeasureStartTs(null);

  useEffect(() => {
    if (!measureStartTs || !connected || !token) return;

    const poll = async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/oss/me/withings/fetch-measures/${fascicoloId}`,
          { startdate: measureStartTs },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMisurazioni(res.data.misurazioni || []);
        const n = res.data.new_count ?? 0;
        if (n > 0) {
          toast.success(`${n} nuova/e misurazione/i salvata/e`);
          setMeasureStartTs(null);
          return true;
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        const isTokenError =
          err.response?.status === 401 ||
          (typeof detail === "string" && detail.includes("Ricollegare l'account"));
        if (isTokenError) {
          toast.error(detail || "Token Withings scaduto.");
          setMeasureStartTs(null);
          return true;
        }
      }
      return false;
    };

    setPolling(true);
    const timeoutId = setTimeout(async () => {
      const done = await poll();
      if (done) clearInterval(intervalId);
    }, 5000);
    const intervalId = setInterval(async () => {
      const done = await poll();
      if (done) clearInterval(intervalId);
    }, 10000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      setPolling(false);
    };
  }, [measureStartTs, connected, fascicoloId, token]);

  const handleFetchLatest = async () => {
    setFetchingLatest(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/oss/me/withings/fetch-latest/${fascicoloId}`,
        {},
        { headers }
      );
      setMisurazioni(res.data.misurazioni || []);
      if ((res.data.new_count ?? 0) > 0) {
        toast.success("Ultima misurazione salvata");
      } else {
        toast.info("Nessuna nuova misurazione negli ultimi 5 minuti");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore recupero dati");
    } finally {
      setFetchingLatest(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const sys = parseInt(String(manualSys).trim(), 10);
    const dia = parseInt(String(manualDia).trim(), 10);
    if (Number.isNaN(sys) || Number.isNaN(dia)) {
      toast.error("Inserire sistolica e diastolica valide");
      return;
    }
    if (sys <= dia) {
      toast.error("La sistolica deve essere maggiore della diastolica");
      return;
    }
    let isoWhen;
    try {
      const d = new Date(manualWhen);
      if (Number.isNaN(d.getTime())) throw new Error("bad");
      isoWhen = d.toISOString();
    } catch {
      toast.error("Data e ora non valide");
      return;
    }
    const hrRaw = String(manualHr).trim();
    let battitoPayload;
    if (hrRaw !== "") {
      const hr = parseInt(hrRaw, 10);
      if (Number.isNaN(hr) || hr < 25 || hr > 220) {
        toast.error("Battito facoltativo: valore tra 25 e 220 bpm");
        return;
      }
      battitoPayload = hr;
    }
    setSavingManual(true);
    try {
      const payload = {
        sistolica: sys,
        diastolica: dia,
        data_misurazione: isoWhen,
        ...(battitoPayload !== undefined ? { battito_cardiaco: battitoPayload } : {}),
      };
      const res = await axios.post(
        `${API_URL}/api/oss/me/withings/manual-measure/${fascicoloId}`,
        payload,
        { headers }
      );
      setMisurazioni(res.data.misurazioni || []);
      toast.success("Misurazione registrata");
      setManualSys("");
      setManualDia("");
      setManualHr("");
      setManualWhen(toLocalDatetimeValue());
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const msg = detail.map((x) => x.msg || x.message || "").filter(Boolean).join(" ");
        toast.error(msg || "Dati non validi");
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore salvataggio");
      }
    } finally {
      setSavingManual(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-base">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
        </CardContent>
      </Card>
    );
  }

  const latest = misurazioni.length > 0 ? misurazioni[0] : null;
  const cls = latest?.classificazione;
  const style = cls ? CLASSIFICATION_STYLES[cls.severity] || CLASSIFICATION_STYLES.normal : null;
  const trend = getTrend(misurazioni);

  const chartData = [...misurazioni].reverse().map((m) => ({
    data: new Date(m.data_misurazione).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }),
    dataFull: new Date(m.data_misurazione).toLocaleString("it-IT"),
    Sistolica: m.sistolica,
    Diastolica: m.diastolica,
    Battito: m.battito_cardiaco,
  }));

  return (
    <Card className="card-base border-rose-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="w-5 h-5 text-rose-500" />
            Monitoraggio Pressione Arteriosa
            <Badge variant="outline" className="ml-2 text-xs font-normal">
              Withings BPM Connect
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {connected ? (
              <Badge className="bg-emerald-500 text-white gap-1">
                <Wifi className="w-3 h-3" />
                Connesso
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-slate-500">
                <WifiOff className="w-3 h-3" />
                Non connesso
              </Badge>
            )}
          </div>
        </div>
        {connectedAt && (
          <p className="text-xs text-slate-400 mt-1">
            Dispositivo della struttura, connesso dal{" "}
            {new Date(connectedAt).toLocaleDateString("it-IT")}
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        <form
          onSubmit={handleManualSubmit}
          className="p-4 rounded-xl border border-slate-200 bg-white space-y-4"
        >
          <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
            <PenLine className="w-4 h-4 text-rose-500" />
            Inserimento manuale
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <div className="space-y-1.5">
              <Label htmlFor="oss-bp-sys" className="text-xs text-slate-600">
                Sistolica (mmHg)
              </Label>
              <Input
                id="oss-bp-sys"
                type="number"
                inputMode="numeric"
                min={40}
                max={300}
                value={manualSys}
                onChange={(e) => setManualSys(e.target.value)}
                placeholder="Es. 120"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oss-bp-dia" className="text-xs text-slate-600">
                Diastolica (mmHg)
              </Label>
              <Input
                id="oss-bp-dia"
                type="number"
                inputMode="numeric"
                min={20}
                max={200}
                value={manualDia}
                onChange={(e) => setManualDia(e.target.value)}
                placeholder="Es. 80"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oss-bp-hr" className="text-xs text-slate-600">
                Battito (opz.)
              </Label>
              <Input
                id="oss-bp-hr"
                type="number"
                inputMode="numeric"
                min={25}
                max={220}
                value={manualHr}
                onChange={(e) => setManualHr(e.target.value)}
                placeholder="Es. 72"
              />
            </div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1 lg:col-span-2">
              <Label htmlFor="oss-bp-when" className="text-xs text-slate-600">
                Data e ora
              </Label>
              <Input
                id="oss-bp-when"
                type="datetime-local"
                value={manualWhen}
                onChange={(e) => setManualWhen(e.target.value)}
                required
              />
            </div>
            <div className="col-span-2 sm:col-span-3 lg:col-span-1 flex sm:justify-end">
              <Button
                type="submit"
                disabled={savingManual}
                variant="secondary"
                className="w-full sm:w-auto border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
              >
                {savingManual ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Registra misurazione"
                )}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Disponibile sempre, con o senza dispositivo Withings collegato.
          </p>
        </form>

        {!connected && (
          <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-sm text-slate-600 flex items-start gap-2">
            <WifiOff className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <span>
              Withings è configurato dall&apos;amministratore. Puoi comunque registrare le misure a
              mano qui sopra.
            </span>
          </div>
        )}

        {!connected && misurazioni.length === 0 && (
          <div className="text-center py-6 text-slate-400 border border-dashed border-slate-200 rounded-xl">
            <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nessuna misurazione in archivio</p>
          </div>
        )}

        {connected && (
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
            {!measureStartTs ? (
              <>
                <Button onClick={handleStartMeasure} className="bg-rose-500 hover:bg-rose-600">
                  <Play className="w-4 h-4 mr-2" />
                  Avvia misurazione
                </Button>
                <div className="h-6 border-l border-slate-300 mx-1" />
                <Button
                  onClick={handleFetchLatest}
                  disabled={fetchingLatest}
                  variant="outline"
                  size="sm"
                  className="text-slate-600"
                >
                  {fetchingLatest ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Download className="w-4 h-4 mr-1" />
                  )}
                  Scarica ultima lettura
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500" />
                  </span>
                  <span className="text-sm font-medium text-rose-600">
                    {polling ? "Controllo automatico ogni 10 secondi..." : "In attesa della misurazione..."}
                  </span>
                </div>
                <Button onClick={handleCancelMeasure} variant="ghost" size="sm" className="text-slate-400 ml-auto">
                  Annulla
                </Button>
              </>
            )}
          </div>
        )}

        {latest && (
          <div className={`rounded-xl p-5 border ${style?.bg || "bg-slate-50"} ${style?.border || "border-slate-200"}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-semibold text-sm ${style?.text || "text-slate-600"}`}>
                Ultima misurazione
              </h4>
              <div className="flex items-center gap-2">
                {cls && <Badge className={`${style?.badge} text-white text-xs`}>{cls.label}</Badge>}
                {trend === "up" && <TrendingUp className="w-4 h-4 text-red-500" />}
                {trend === "down" && <TrendingDown className="w-4 h-4 text-green-500" />}
                {trend === "stable" && <Minus className="w-4 h-4 text-slate-400" />}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">{latest.sistolica ?? "—"}</p>
                <p className="text-xs text-slate-500 mt-1">Sistolica (mmHg)</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">{latest.diastolica ?? "—"}</p>
                <p className="text-xs text-slate-500 mt-1">Diastolica (mmHg)</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Heart className="w-5 h-5 text-rose-500" />
                  <p className="text-3xl font-bold text-slate-900">{latest.battito_cardiaco ?? "—"}</p>
                </div>
                <p className="text-xs text-slate-500 mt-1">Battito (bpm)</p>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 justify-end">
              <Clock className="w-3 h-3 text-slate-400" />
              <span className="text-xs text-slate-400">
                {new Date(latest.data_misurazione).toLocaleString("it-IT")}
              </span>
            </div>
          </div>
        )}

        {chartData.length >= 2 && (
          <div>
            <h4 className="font-semibold text-sm text-slate-600 mb-3">Andamento nel tempo</h4>
            <div className="w-full" style={{ height: 256 }}>
              <ResponsiveContainer width="100%" height={256}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="data" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.dataFull || ""}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }} />
                  <Line type="monotone" dataKey="Sistolica" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Diastolica" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Battito" stroke="#a855f7" strokeWidth={1.5} strokeDasharray="4 2" dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {misurazioni.length > 0 && (
          <div>
            <h4 className="font-semibold text-sm text-slate-600 mb-3">Storico misurazioni</h4>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Data e Ora</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-600">Sistolica</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-600">Diastolica</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-600">Battito</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-600">Stato</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-600">Origine</th>
                  </tr>
                </thead>
                <tbody>
                  {misurazioni.map((m, idx) => {
                    const rowCls = m.classificazione;
                    const rowStyle = rowCls ? CLASSIFICATION_STYLES[rowCls.severity] : null;
                    return (
                      <tr key={m.id || idx} className="border-b last:border-b-0 hover:bg-slate-50">
                        <td className="px-4 py-2.5 text-slate-700">
                          {new Date(m.data_misurazione).toLocaleString("it-IT", {
                            day: "2-digit", month: "2-digit", year: "numeric",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </td>
                        <td className="text-center px-4 py-2.5 font-semibold">{m.sistolica ?? "—"}</td>
                        <td className="text-center px-4 py-2.5 font-semibold">{m.diastolica ?? "—"}</td>
                        <td className="text-center px-4 py-2.5">{m.battito_cardiaco ?? "—"}</td>
                        <td className="text-center px-4 py-2.5">
                          {rowCls ? (
                            <Badge className={`${rowStyle?.badge} text-white text-[10px]`}>{rowCls.label}</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="text-center px-4 py-2.5 text-slate-500 text-xs">
                          {m.origine === "manuale" ? "Manuale" : "Withings"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
